import { useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Grid,
  LoadingOverlay,
  ScrollArea,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { CREATE_Shipping } from "apollo/mutuations";
import { GET_MY_WARE_HOUSES, GET_WARE_HOUSE_SHIPPING } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";

const ShippingAddModal = ({ setOpened, size, activePage }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tempWarhouse, setTempWarehouse] = useState([]);
  const form = useForm({
    initialValues: {
      warehouse_id: "",
      category_id: "",
      period_type: "",
      min_period: null,
      max_period: null,
    },
    validate: {
      min_period: (value) => {
        if (value === null || value === "" || value === undefined)
          return "Min period is required";
        if (isNaN(Number(value))) return "Must be a number";
        if (Number(value) < 0) return "Min period can't be negative";
        if (Number(value) === 0) return "Min period can't be zero";
        return null;
      },
      max_period: (value) => {
        if (value === null || value === "" || value === undefined)
          return "Max period is required";
        if (isNaN(Number(value))) return "Must be a number";
        if (Number(value) < 0) return "Max period can't be negative";
        if (Number(value) === 0) return "Max period can't be zero";
        return null;
      },
    },
  });
  const { loading: supplierLoading, refetch } = useQuery(GET_MY_WARE_HOUSES, {
    variables: {
      first: parseInt(1000),
      page: 1,
    },

    onCompleted(data) {
      const arr = data.myWarehouses.data
        .filter((item) => item.status === "ACTIVE")
        .map((item) => ({
          label: item.name,
          value: item.id,
        }));

      setWarehouses(arr);
      setTempWarehouse(data.myWarehouses.data);
    },
  });
  const [business, setBusiness] = useState([]);
  useEffect(() => {
    refetch();
  }, []);

  const [addShipping, { loading: warehouseManagerLoading }] = useMutation(
    CREATE_Shipping,
    {
      update(cache, { data: { createWarehouseShippingEstimation } }) {
        cache.updateQuery(
          {
            query: GET_WARE_HOUSE_SHIPPING,
            variables: {
              first: parseInt(size),
              page: activePage,
              search: "",
              warehouse_id: "",
              ordered_by: [
                {
                  column: "CREATED_AT",
                  order: "DESC",
                },
              ],
            },
          },
          (data) => {
            if (data) {
              return {
                myWarehouseShippingEstimations: {
                  ...data.myWarehouseShippingEstimations,
                  data: [
                    createWarehouseShippingEstimation,
                    ...data.myWarehouseShippingEstimations.data,
                  ],
                },
              };
            }
          },
        );
      },
    },
  );

  const { height } = useViewportSize();
  const submit = () => {
    if (form.validate().hasErrors) {
      return;
    }
    addShipping({
      variables: {
        warehouse_id: form.getInputProps("warehouse_id").value,
        category_id: form.getInputProps("category_id").value,
        period_type: form.getInputProps("period_type").value,
        min_period: parseInt(form.getInputProps("min_period").value),
        max_period: parseInt(form.getInputProps("max_period").value),
      },
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Success",
          message: "Shipping Estimation Created Successfully",
        });
        setOpened(false);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Shipping Estimation Not Created Successfully",
        });
      },
    });
  };
  const handleCategoryChange = (val) => {
    form.setFieldValue("category_id", val); // Reset subcategory
  };
  const setWarehouseDropDownValue = (val) => {
    form.setFieldValue("warehouse_id", val);
    const selectedWarehouse = tempWarhouse.find((wh) => wh.id === val);
    const categories =
      selectedWarehouse?.supplier?.categories?.map((item) => item.category) ||
      [];
    setCategories(categories);
  };

  return (
    <>
      <LoadingOverlay
        visible={warehouseManagerLoading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(() => submit())}>
          <Stack>
            <Grid>
              <Grid.Col span={4}>
                <Select
                  data={warehouses}
                  value={form.getInputProps("warehouse_id")?.value?.toString()}
                  onChange={setWarehouseDropDownValue}
                  label="Warehouse"
                  placeholder="Pick a warehouse"
                  searchable
                />

                <Select
                  searchable
                  data={categories.map((sub) => ({
                    label: sub.name,
                    value: sub.id,
                  }))}
                  value={form.getInputProps("category_id").value?.toString()}
                  onChange={handleCategoryChange}
                  label="Category"
                  placeholder="Pick a Category"
                  disabled={categories.length === 0}
                />
              </Grid.Col>

              <Grid.Col span={2}>
                <Select
                  data={["DAY", "WEEK", "HOUR"]}
                  label="Period type"
                  placeholder="Period type"
                  {...form.getInputProps("period_type")}
                />
              </Grid.Col>
              <Grid.Col span={2}>
                <TextInput
                  required
                  type="number"
                  label="Min period"
                  placeholder="Min period"
                  {...form.getInputProps("min_period")}
                />
              </Grid.Col>
              <Grid.Col span={2}>
                <TextInput
                  required
                  type="number"
                  label="Max period"
                  placeholder="Max period"
                  {...form.getInputProps("max_period")}
                />
              </Grid.Col>
            </Grid>
            <Grid>
              <Grid.Col span={4}>
                <Button
                  style={{
                    width: "25%",
                    marginTop: "15px",
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  type="submit"
                  fullWidth
                >
                  Submit
                </Button>
              </Grid.Col>
            </Grid>
          </Stack>
        </form>
      </ScrollArea>
    </>
  );
};

export default ShippingAddModal;
